/**
 * Sekcje pulpitu jako komponenty sterowane propsami (bez własnych zapytań).
 * Moduły mogą ich używać we własnych widgetach (`FrontendModule.dashboardWidgets`).
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, Card, CardHeader, CardList, CardListItem, ConfidenceBar, EmptyState, StatusChip } from '@/design/components'
import type { StatusVariant } from '@/design/components'
import { dateBadge, formatDueLabel, formatRelative, daysUntil } from '@/lib/format'
import { cn } from '@/lib/cn'

/* ---------------------------------------------------- dokumenty z AI */

export interface AiDocumentItem {
  id: string | number
  title: string
  /** np. „e-Doręczenia · Postanowienie · wykryty termin 21.08" */
  meta?: string
  /** sygnatura sprawy (monospace) albo null gdy brak dopasowania */
  signature?: string | null
  /** 0..1 lub 0..100 */
  confidence?: number | null
  onClick?: () => void
}

export interface AiDocumentsCardProps {
  items: AiDocumentItem[]
  /** Link „Skrzynka →" w nagłówku (trasa z /api/me/context). */
  moreHref?: string
  loading?: boolean
}

/** „Dokumenty rozpoznane przez AI — do weryfikacji" (pulpit). */
export function AiDocumentsCard({ items, moreHref, loading = false }: AiDocumentsCardProps) {
  return (
    <Card>
      <CardHeader
        title="Dokumenty rozpoznane przez AI — do weryfikacji"
        action={moreHref ? <HeaderLink to={moreHref}>Skrzynka →</HeaderLink> : null}
      />
      {loading ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <EmptyState title="Brak dokumentów do weryfikacji" description="Nowe pisma pojawią się tu po analizie OCR i modelu." />
      ) : (
        <CardList>
          {items.map((item) => (
            <CardListItem key={item.id} onClick={item.onClick}>
              <div className="flex items-center gap-3.5">
                <DocumentThumb />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-ink">{item.title}</p>
                  {item.meta ? <p className="mt-0.5 truncate text-small text-muted">{item.meta}</p> : null}
                </div>
                <span className="t-mono hidden w-[104px] shrink-0 text-right text-muted lg:block">
                  {item.signature ?? '—'}
                </span>
                <ConfidenceBar value={item.confidence} label="prefixed" width={96} className="shrink-0" />
              </div>
            </CardListItem>
          ))}
        </CardList>
      )}
    </Card>
  )
}

/* --------------------------------------------------------- terminy */

export interface DeadlineItem {
  id: string | number
  date: string | Date
  title: string
  /** np. „IV P 76/25 · Medica Grupa" */
  meta?: string
  onClick?: () => void
}

export interface DeadlinesCardProps {
  items: DeadlineItem[]
  moreHref?: string
  loading?: boolean
  title?: string
}

/** Wariant chipa terminu: ≤3 dni — czerwony, ≤14 — bursztyn, dalej — neutralny. */
export function dueVariant(date: string | Date | null | undefined): StatusVariant {
  const days = daysUntil(date)
  if (days === null) return 'neutral'
  if (days <= 3) return 'danger'
  if (days <= 14) return 'warning'
  return 'neutral'
}

/** „Najbliższe terminy" (prawa kolumna pulpitu). */
export function DeadlinesCard({ items, moreHref, loading = false, title = 'Najbliższe terminy' }: DeadlinesCardProps) {
  return (
    <Card>
      <CardHeader title={title} action={moreHref ? <HeaderLink to={moreHref}>Wszystkie →</HeaderLink> : null} />
      {loading ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <EmptyState title="Brak nadchodzących terminów" />
      ) : (
        <CardList>
          {items.map((item) => {
            const badge = dateBadge(item.date)
            return (
              <CardListItem key={item.id} onClick={item.onClick}>
                <div className="flex items-center gap-3.5">
                  <span className="flex h-[38px] w-[40px] shrink-0 flex-col items-center justify-center rounded border border-line-soft">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">{badge.month}</span>
                    <span className="font-display text-[16px] leading-none text-ink">{badge.day}</span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-semibold text-ink">{item.title}</p>
                    {item.meta ? <p className="mt-0.5 truncate text-small text-muted">{item.meta}</p> : null}
                  </div>
                  <StatusChip variant={dueVariant(item.date)} size="sm" className="shrink-0">
                    {formatDueLabel(item.date)}
                  </StatusChip>
                </div>
              </CardListItem>
            )
          })}
        </CardList>
      )}
    </Card>
  )
}

/* ------------------------------------------------------- aktywność */

export interface ActivityItem {
  id: string | number
  /** Autor — inicjały na avatarze; dla wpisów systemowych podaj `initials: 'AI'`. */
  actor?: string | null
  initials?: string
  text: ReactNode
  at: string | Date
}

export function ActivityCard({ items, loading = false }: { items: ActivityItem[]; loading?: boolean }) {
  return (
    <Card>
      <CardHeader title="Ostatnia aktywność" />
      {loading ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <EmptyState title="Brak zdarzeń" description="Tu pojawi się historia działań w kancelarii." />
      ) : (
        <CardList>
          {items.map((item) => (
            <CardListItem key={item.id}>
              <div className="flex items-start gap-3">
                <Avatar name={item.actor} initials={item.initials} size="sm" tone="light" className="mt-0.5" />
                <div className="min-w-0">
                  <p className="text-base leading-snug text-ink">{item.text}</p>
                  <p className="mt-0.5 text-small text-muted">{formatRelative(item.at)}</p>
                </div>
              </div>
            </CardListItem>
          ))}
        </CardList>
      )}
    </Card>
  )
}

/* ------------------------------------------------------ moje sprawy */

export interface MyCaseItem {
  id: string | number
  signature: string
  title: string
  status?: { label: string; variant?: StatusVariant }
  onClick?: () => void
}

export function MyCasesCard({
  items,
  moreHref,
  loading = false,
}: {
  items: MyCaseItem[]
  moreHref?: string
  loading?: boolean
}) {
  return (
    <Card>
      <CardHeader title="Moje sprawy" action={moreHref ? <HeaderLink to={moreHref}>Wszystkie →</HeaderLink> : null} />
      {loading ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <EmptyState title="Nie prowadzisz żadnej sprawy" />
      ) : (
        <CardList>
          {items.map((item) => (
            <CardListItem key={item.id} onClick={item.onClick}>
              <div className="flex items-center justify-between gap-3">
                <span className="t-mono text-ink">{item.signature}</span>
                {item.status ? (
                  <StatusChip variant={item.status.variant ?? 'info'} size="sm">
                    {item.status.label}
                  </StatusChip>
                ) : null}
              </div>
              <p className="mt-1.5 truncate text-base text-ink">{item.title}</p>
            </CardListItem>
          ))}
        </CardList>
      )}
    </Card>
  )
}

/* --------------------------------------------------------- wspólne */

function HeaderLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-micro text-muted transition-colors hover:text-ink">
      {children}
    </Link>
  )
}

function DocumentThumb({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'block h-[30px] w-[26px] shrink-0 rounded-[2px] border border-line-soft bg-[repeating-linear-gradient(135deg,#eef1f4_0_4px,#f7f9fa_4px_8px)]',
        className,
      )}
    />
  )
}

function SkeletonRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="divide-y divide-line-soft">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 px-5 py-4">
          <span className="h-[30px] w-[26px] shrink-0 animate-pulse rounded bg-[#eef1f4]" />
          <span className="h-3 w-1/2 animate-pulse rounded bg-[#eef1f4]" />
        </div>
      ))}
    </div>
  )
}
